/**
 * Helpers for executing commands on a remote server
 */

import { type Deployment, DeploymentState, type Instance } from '@clovyr/pollen';
import {
  CLOVYR_CONFIGURE_KEY,
  CLOVYR_CUSTOM_DOMAIN,
  type Dict,
  type Manifest,
} from '@clovyr/pollen/manifest';
import { isDNSProxied } from '@clovyr/pollen/manifest/configure/util';
import {
  authenticateAppHost,
  exec,
  execWithAsyncOutput,
  execWithOutput,
} from '@clovyr/pollen/remote_exec';

import { usePollenStore } from '@/stores/pollen_store';

function isInstance(src: Instance | Deployment): src is Instance {
  return (src as Instance).configurekey !== undefined;
}
function isDeployment(src: Instance | Deployment): src is Deployment {
  return (src as Deployment).hostingProvider !== undefined;
}

const hostCache: Record<string, string> = {};
const hostLoginCache: Record<string, Promise<boolean>> = {};

async function hostLoginIfNeeded(host: string, accessKey: string): Promise<boolean> {
  const cacheKey = `${host}:${accessKey}`;
  if (!hostLoginCache[cacheKey]) {
    hostLoginCache[cacheKey] = authenticateAppHost(host, accessKey);
  }
  return hostLoginCache[cacheKey];
}

interface RemoteExecOptions {
  allowNonRunning?: boolean;
}

export const useRemoteExec = (
  src: Instance | Deployment,
  app?: Manifest,
  values?: Dict,
  options: RemoteExecOptions = {},
) => {
  const { getDeploymentApp, getDeploymentByFQDN } = usePollenStore();

  const creds = ref({
    host: '',
    accessKey: '',
  });

  // default is proxied. only disable if manifest requests or using custom domain
  let isPrimaryProxied = true;
  let originalHost = '';
  const isDeploymentSource = isDeployment(src);
  const remoteAccessAllowed =
    isInstance(src) ||
    options.allowNonRunning ||
    src.state === DeploymentState.Running ||
    src.state === DeploymentState.Updating;

  if (isInstance(src)) {
    if (!app) {
      const dep = getDeploymentByFQDN(src.fqdn!);
      if (dep) {
        app = getDeploymentApp(dep);
      }
    }
    if (app) {
      isPrimaryProxied = isDNSProxied(app, values?.[CLOVYR_CUSTOM_DOMAIN] as string);
    }
    originalHost = src.fqdn!;
    if (remoteAccessAllowed) {
      creds.value.host = originalHost;
      creds.value.accessKey = src.configurekey!;
    }
  } else if (isDeployment(src)) {
    if (!app) {
      app = getDeploymentApp(src);
    }
    if (app) {
      isPrimaryProxied = isDNSProxied(
        app,
        (src.appSettings?.[CLOVYR_CUSTOM_DOMAIN] as string) ?? src.customDomain,
      );
    }
    originalHost = src.fqdn!;
    if (remoteAccessAllowed) {
      creds.value.host = originalHost;
      creds.value.accessKey = src.accessList?.[0];
      if (!creds.value.accessKey) {
        // during an interrupted launch, may be in appSettings instead of accessList
        creds.value.accessKey = src.appSettings?.[CLOVYR_CONFIGURE_KEY] as string;
      }
    }
  }

  if (creds.value.host && !isPrimaryProxied) {
    creds.value.host =
      isDeploymentSource && hostCache[src.id] ? hostCache[src.id] : `cfproxy-${originalHost}`;
  }

  async function ensureHostLogin(): Promise<boolean> {
    if (!creds.value.host || !creds.value.accessKey) {
      return false;
    }

    if (isPrimaryProxied) {
      return hostLoginIfNeeded(creds.value.host, creds.value.accessKey);
    }

    try {
      await hostLoginIfNeeded(creds.value.host, creds.value.accessKey);
      await exec(creds.value.host, creds.value.accessKey, 'echo alive', true, false);
      if (isDeploymentSource) {
        hostCache[src.id] = creds.value.host;
      }
      return true;
    } catch {
      const fallbackHost = originalHost;
      if (!fallbackHost || fallbackHost === creds.value.host) {
        return false;
      }

      creds.value.host = fallbackHost;
      if (isDeploymentSource) {
        hostCache[src.id] = creds.value.host;
      }
      return hostLoginIfNeeded(creds.value.host, creds.value.accessKey);
    }
  }

  async function assertReadyForRemoteExec() {
    if (!(await ensureHostLogin())) {
      throw new Error('Deployment is not ready for remote execution');
    }
  }

  return {
    creds,
    ...creds.value,
    ensureHostLogin,

    exec: async (script: string, generic?: boolean, verbose?: boolean, async?: boolean) => {
      await assertReadyForRemoteExec();
      return exec(creds.value.host, creds.value.accessKey, script, generic, verbose, async);
    },

    execWithOutput: async (script: string) => {
      await assertReadyForRemoteExec();
      return execWithOutput(creds.value.host, creds.value.accessKey, script);
    },

    execWithAsyncOutput: async (script: string) => {
      await assertReadyForRemoteExec();
      return execWithAsyncOutput(creds.value.host, creds.value.accessKey, script);
    },
  };
};
